import { __read, __spread } from "tslib";
import getPaymentMessage from '../lib/get-payment-message';
import { FrameMessageAbortReason, FrameMessageType } from '../types';
function getDuplicateMessageChecker() {
    var lastMessage = '';
    var toPlain = function (msg) {
        return msg.source + ":" + msg.type + ":" + JSON.stringify(msg.data);
    };
    return function (msg) {
        var message = toPlain(msg);
        var isSame = lastMessage === message;
        lastMessage = message;
        return isSame;
    };
}
function createChainMessageHandler(rules, onMatch) {
    var stack = [];
    return function (message) {
        if (rules[stack.length](message)) {
            stack.push(message);
            if (stack.length === rules.length) {
                onMatch.apply(void 0, __spread(stack));
                stack = [];
            }
        }
    };
}
var normalizeCardId = function (cardId) {
    return cardId.startsWith('card-x') ? cardId : "card-x" + cardId;
};
function createLoadedMessage() {
    return {
        type: FrameMessageType.paymentLoaded
    };
}
function createSuccessMessage(payment) {
    if (payment.card_id) {
        return {
            type: FrameMessageType.success,
            cardId: normalizeCardId(payment.card_id),
            payment: payment
        };
    }
    return {
        type: FrameMessageType.success,
        payment: payment
    };
}
function createErrorMessage(reason) {
    return {
        type: FrameMessageType.error,
        reason: reason
    };
}
function createAbortMessage(reason) {
    return {
        type: FrameMessageType.abort,
        reason: reason
    };
}
function createResizeMessage(size) {
    return {
        type: FrameMessageType.resize,
        size: {
            width: size.width,
            height: size.height
        }
    };
}
function createDiscountMessage(data) {
    return {
        type: FrameMessageType.discount,
        data: {
            amount: data.amount,
            originalAmount: data.original_amount,
            discounts: data.discounts,
            promocode: data.promocode
        }
    };
}
function createCallActionMessage(data) {
    return {
        type: FrameMessageType.callActionResponse,
        data: data
    };
}
function createFrameMessagesHandler(onMessage) {
    var checkoutHandlers = [
        createChainMessageHandler([
            function (msg) { return msg.type === FrameMessageType.initCheckout; },
            function (msg) { return msg.type === FrameMessageType.paymentLoaded; }
        ], function () { return onMessage(createLoadedMessage()); }),
        createChainMessageHandler([
            function (msg) { return msg.type === FrameMessageType.initCheckout; },
            function (msg) { return msg.type === FrameMessageType.paymentComplete; }
        ], function (_, msg) { return onMessage(createSuccessMessage(msg.data)); }),
        createChainMessageHandler([
            function (msg) { return msg.type === FrameMessageType.initCheckout; },
            function (msg) { return msg.type === FrameMessageType.paymentFail; }
        ], function (_, msg) { return onMessage(createErrorMessage(msg.data.status)); }),
        createChainMessageHandler([function (msg) { return msg.type === FrameMessageType.paymentTimeout; }], function () { return onMessage(createAbortMessage(FrameMessageAbortReason.timeout)); }),
        createChainMessageHandler([function (msg) { return msg.type === FrameMessageType.paymentCancel; }], function () { return onMessage(createAbortMessage(FrameMessageAbortReason.user)); }),
        createChainMessageHandler([function (msg) { return msg.type === FrameMessageType.onResize; }], function (msg) { return onMessage(createResizeMessage(msg.data)); }),
        createChainMessageHandler([function (msg) { return msg.type === FrameMessageType.paymentPreview; }], function (msg) { return onMessage(createDiscountMessage(msg.data)); }),
        createChainMessageHandler([function (msg) { return msg.type === FrameMessageType.callActionResponse; }], function (msg) { return onMessage(createCallActionMessage(msg.data)); })
    ];
    var oldFormHandlers = [
        createChainMessageHandler([
            function (msg) { return msg.type === 'payment-status' && msg.data.value === 'success'; },
            function (msg) {
                return msg.type === 'payment' && msg.data.binding_result !== 'binding_error';
            }
        ], function (msg1, msg2) { return onMessage(createSuccessMessage(msg2.data)); }),
        createChainMessageHandler([
            function (msg) { return msg.type === '3ds-status' && msg.data === '3ds-success'; },
            function (msg) {
                return msg.type === 'payment' && msg.data.binding_result !== 'binding_error';
            }
        ], function (msg1, msg2) { return onMessage(createSuccessMessage(msg2.data)); }),
        createChainMessageHandler([
            function (msg) {
                return msg.type === 'payment-status' &&
                    msg.data.value === 'wait_for_notification';
            },
            function (msg) {
                return msg.type === 'payment-status' &&
                    msg.data.value !== 'wait_for_notification';
            },
            function (msg) { return msg.type === 'payment-check-failure'; }
        ], function (msg1, msg2) { return onMessage(createErrorMessage(msg2.data.value)); }),
        createChainMessageHandler([function (msg) { return msg.type === 'payment-update-failure'; }], function () { return onMessage(createErrorMessage('request_failure')); }),
        createChainMessageHandler([function (msg) { return msg.type === '3ds-status' && msg.data === '3ds-failure'; }], function () { return onMessage(createErrorMessage('3ds_failure')); }),
        createChainMessageHandler([
            function (msg) {
                return msg.type === 'payment' && msg.data.binding_result === 'binding_error';
            }
        ], function () { return onMessage(createErrorMessage('binding_error')); }),
        createChainMessageHandler([function (msg) { return msg.type === 'payment-status' && msg.data.value === 'abort'; }], function () { return onMessage(createAbortMessage(FrameMessageAbortReason.user)); }),
        createChainMessageHandler([
            function (msg) { return [
                'init-checkout',
                'form-ready',
                'form-status',
                'form-submit',
                'auth-start',
                'auth-end'
            ].includes(msg.type); }
        ], function (msg) { return onMessage({ type: msg.type, data: msg.data }); })
    ];
    var handlers = __spread(checkoutHandlers, oldFormHandlers);
    return function (message) { return handlers.forEach(function (handler) { return handler(message); }); };
}
export default function listenPayment(onMessage) {
    var handleFrameMessage = createFrameMessagesHandler(onMessage);
    var isDuplicate = getDuplicateMessageChecker();
    var handlePostMessage = function (e) {
        var message = getPaymentMessage(e.data);
        if (message && !isDuplicate(message)) {
            handleFrameMessage(message);
        }
    };
    window.addEventListener('message', handlePostMessage);
    return function () {
        window.removeEventListener('message', handlePostMessage);
    };
}
